import React, { ChangeEvent, Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined, definedAndNotNull } from "@formant/core/common/defined";
import { mergeDeviceConfig } from "@formant/core/common/mergeDeviceConfig";
import { throttle } from "@formant/core/common/throttle";
import { duration } from "@formant/core/model/duration";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationTemplate } from "@formant/core/model/IDeviceConfigurationTemplate";
import { IDeviceQuery } from "@formant/core/model/IDeviceQuery";
import { ITags } from "@formant/core/model/ITags";
import { Uuid } from "@formant/core/model/Uuid";
import { InputElement } from "@formant/ui/component/common/Field";

import { track } from "../../../analytics";
import { deviceClient } from "../../../clients";
import {
    appStore,
    authStore,
    deviceConfigurationTemplateStore,
    deviceDetailsStore,
    deviceStore,
    routerStore,
} from "../../../stores";
import { hashObservable } from "../../../utils/hashObservable";
import { ButtonsBar } from "../../common/ButtonsBar";
import { Help } from "../../common/Help";
import { IconButton } from "../../common/IconButton";
import { SelectDeviceTags } from "../../common/SelectDeviceTags";
import { SelectField } from "../../common/SelectField";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { Timer } from "../../common/Timer";
import { SearchField } from "../../SearchField";
import { SettingsPage } from "../SettingsPage";
import { DevicesTable } from "./DevicesTable";
import * as styles from "./index.scss";

const perPage = 50;
@observer
export class Devices extends Component {
    @observable
    private query: string = "";
    @observable
    private queryField: string = "";
    @observable
    private tags: ITags = {};
    @observable
    private selectedIds: Uuid[] = [];
    @observable
    private page = 0;
    @hashObservable()
    private applyId: Uuid | undefined = undefined;
    private configuration?: IDeviceConfigurationTemplate;
    private throttledUpdateQuery = throttle((query: string) => {
        this.query = query;
        this.page = 0;
    }, 0.5 * duration.second);

    public render() {
        const { tags, selectedIds, applyId, page } = this;
        const { routeTo } = routerStore;

        const deviceConfigurationTemplates =
            (applyId && deviceConfigurationTemplateStore.list()) || [];

        const configuration = applyId
            ? deviceConfigurationTemplates.find(_ => _.id === applyId)
            : undefined;
        this.configuration = configuration;

        // The user cannot overwrite any tags used by the configuration filter.
        const configurationTagKeys =
            configuration?.tags && Object.keys(configuration.tags);

        const allDevicesCount = deviceStore.count({
            enabled: true,
            type: "default",
        });

        const filteredDevices = this.getDevices({ pagination: true });
        const allDevices = this.getDevices({ pagination: false });

        const devices =
            allDevices.length <= perPage ? allDevices : filteredDevices;

        const paginationTotal =
            allDevices.length > perPage ? allDevices.length : devices.length;

        return (
            <SettingsPage
                title="Devices"
                className={classnames(
                    styles.devices,
                    applyId && styles.applying,
                )}
                extra={
                    <Help
                        text="A device is a robot which ingests data to the Formant cloud. Administrators can create, update, provision, deprovision, and delete devices. Click this icon for documentation."
                        to="https://docs.formant.io/docs/getting-started-setting-up-your-device"
                    />
                }
                actions={
                    !applyId && (
                        <>
                            <StandardButton to={routeTo.bulkProvision()}>
                                Bulk Provision
                            </StandardButton>
                            <StandardButton
                                className={styles.add}
                                to={routeTo.createDevice()}
                            >
                                Add device
                            </StandardButton>
                        </>
                    )
                }
            >
                <StatsHeader
                    icon="device"
                    loading={allDevicesCount === undefined}
                    totalQuantity={allDevicesCount || 0}
                    totalLabel="Devices"
                />
                <div className={styles.filter}>
                    <SearchField
                        value={this.queryField}
                        onChange={this.onChangeQueryField}
                        className={styles.search}
                    />
                    <SelectDeviceTags
                        value={tags}
                        includeNone={true}
                        excludeTagKeys={configurationTagKeys}
                        className={styles["select-tags"]}
                        onChange={this.onChangeTags}
                    />
                </div>
                <DevicesTable
                    devices={devices}
                    allDevices={allDevices}
                    selectMode={!!applyId}
                    selectedIds={selectedIds}
                    page={page}
                    perPage={perPage}
                    count={paginationTotal}
                    onChangePage={this.onChangePage}
                    onUpdateSelected={this.onUpdateSelected}
                />
                <ButtonsBar
                    fixed={true}
                    collapsed={!applyId}
                    extra={
                        <>
                            <div className={styles.selected}>
                                <div className={styles.count}>
                                    {selectedIds.length}
                                </div>
                                <div className={styles.description}>
                                    Devices selected
                                </div>
                            </div>
                            <div className={styles.configuration}>
                                <div className={styles.text}>Configuration</div>
                                <SelectField
                                    label=""
                                    value={applyId}
                                    small={true}
                                    className={styles.select}
                                    onChange={this.onChangeApplyId}
                                >
                                    {deviceConfigurationTemplates.map(
                                        ({ id, name }) => (
                                            <option value={id} key={id}>
                                                {name}
                                            </option>
                                        ),
                                    )}
                                </SelectField>
                            </div>
                        </>
                    }
                >
                    <StandardButton
                        primary={true}
                        large={true}
                        onClicked={this.onApplyConfiguration}
                    >
                        Apply
                    </StandardButton>
                    <IconButton
                        icon="close"
                        onClicked={this.onClose}
                        className={styles.close}
                    />
                </ButtonsBar>
                <Timer onTick={this.onTick} interval={5 * duration.second} />
            </SettingsPage>
        );
    }

    public componentWillUnmount() {
        this.applyId = undefined;
        const allDevicesCount = deviceStore.count({
            enabled: true,
            type: "default",
        });
        track("Number of Devices", { Count: allDevicesCount });
    }

    private getDevices(options = { pagination: true }): IDevice[] {
        const { query, page } = this;

        const matched = deviceDetailsStore.listMatching(
            {
                ...this.tags,
                ...(this.configuration?.tags || {}),
            },
            options.pagination
                ? {
                      enabled: true,
                      type: "default",
                      count: perPage,
                      offset: page * perPage,
                      query,
                  }
                : {
                      enabled: true,
                      type: "default",
                      count: undefined,
                      offset: 0,
                      query,
                  },
        );

        return matched || [];
    }

    private onChangePage = (page: number) => {
        this.page = page;
    };

    private onChangeQueryField = (query: string) => {
        this.queryField = query;
        this.throttledUpdateQuery(query);
    };

    private onChangeTags = (tags: ITags) => {
        this.tags = tags;
        this.page = 0;
    };

    private onUpdateSelected = (selectedIds: Uuid[]) => {
        this.selectedIds = selectedIds;
    };

    private onChangeApplyId = (event: ChangeEvent<InputElement>) => {
        this.applyId = event.target.value as Uuid;
    };

    private onTick = async () => {
        const { query, page } = this;

        await deviceStore.fetchList({
            enabled: true,
            type: "default",
            count: perPage,
            offset: page * perPage,
            query,
        });
    };

    private onClose = () => {
        this.applyId = undefined;
        this.selectedIds = [];
    };

    private onApplyConfiguration = async () => {
        const { selectedIds, configuration } = this;
        if (!configuration) {
            return;
        }

        const query: IDeviceQuery = {
            enabled: true,
            type: "default",
        };

        const devices = await deviceDetailsStore.listAsync(query);

        const { document } = configuration;
        const token = await authStore.token();

        const results = await Promise.all(
            devices
                .filter(({ id }) => selectedIds.some(_ => _ === id))
                .map(async device => {
                    const id = defined(device.id);
                    // merge the template over the device's current config so applying it preserves sections the template omits (e.g. teleop); the full merged doc is saved via PUT. The device-list item carries no config version, so fetch the full device for desiredConfigurationVersion, then the current config document.
                    const { desiredConfigurationVersion } = defined(
                        await deviceClient.get(token, id),
                    );
                    const current = await deviceClient.getConfiguration(
                        token,
                        id,
                        definedAndNotNull(desiredConfigurationVersion),
                    );
                    await deviceClient.updateConfiguration(token, id, {
                        deviceId: id,
                        document: mergeDeviceConfig(current.document, {
                            ...document,
                            tags: device.tags,
                        }),
                    });
                }),
        );

        appStore.setSnackbar(
            `Applied configuration ${configuration.name} to ${
                results.length
            } device${results.length > 1 ? "s" : ""}`,
        );

        this.selectedIds = [];
        this.applyId = undefined;
    };
}
